import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { JobPost, PostStage } from "@/pages/_layouts/jobs/types";
import { toDateFormat, toDateTimeFormat } from "@/lib/dateHelpers";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { formatMarkdownText, markdownComponents } from "@/lib/markdownHelpers";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import DetailsCard from "@/components/DetailsCard";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { generatePath } from "react-router";
import { Path } from "@/router";
import { useQuery } from "@apollo/client";
import { JOB_POST_VIEWS_QUERY } from "../queries";

interface JobDetailsCardsProps {
  jobPost: JobPost;
}

function MarkdownContent({ content, preserveHeadingStyle }: { content: string; preserveHeadingStyle?: boolean }) {
  return (
    <div
      className={`
      [&_ul]:list-disc
      [&_ul]:list-inside
      [&_li]:my-1
      ${preserveHeadingStyle ? "[&_p]:!text-body-large [&_p]:!break-all [&_p]:!font-inherit" : ""}
    `}
    >
      <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
        {formatMarkdownText(content || "")}
      </ReactMarkdown>
    </div>
  );
}

function formatAddress(mainAddress: any) {
  if (!mainAddress) return "";

  const { address1, city, state, postalCode } = mainAddress;
  const parts = [address1, `${city}, ${state} ${postalCode}`].filter(Boolean);
  return parts.join(" ");
}

export function JobDetailsCards({ jobPost }: JobDetailsCardsProps) {
  const emptyOptions = ["null", "None"];
  const canManageJobPosts = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });
  const {
    data: viewsData,
    loading: viewsLoading,
    error: viewsError
  } = useQuery(JOB_POST_VIEWS_QUERY, {
    variables: { jobPostId: jobPost.id },
    skip: !jobPost.id,
    errorPolicy: "all"
  });
  const uniqueViews = viewsData?.jobPost?.views;
  // Soft-fail success payloads can return views: null with no GraphQL error — treat like Unavailable.
  const viewsUnavailable = !viewsLoading && (viewsError != null || uniqueViews == null);
  const uniqueViewsStatus = uniqueViews != null ? String(uniqueViews) : viewsLoading ? "…" : "Unavailable";
  const uniqueViewsSrOnly = viewsLoading
    ? "Loading unique views"
    : viewsUnavailable
      ? "Unique views unavailable"
      : undefined;
  const uniqueViewsHeadline = (
    <span aria-live="polite" aria-busy={viewsLoading || undefined}>
      {uniqueViewsSrOnly ? <span className="sr-only">{uniqueViewsSrOnly}</span> : null}
      {uniqueViewsStatus}
    </span>
  );

  const getApplicationUrl = () => {
    switch (jobPost.applyVia) {
      case "Facility Website":
        return jobPost.externalJobUrl;
      case "PGA.org / Email Notification":
        return jobPost.pgaOrgUrl;
      default:
        return jobPost.pgaOrgUrl;
    }
  };

  const applicationUrl = getApplicationUrl();

  const baseCompensationDisplay =
    jobPost.compensation?.baseMinimum === jobPost.compensation?.baseMaximum
      ? formatDisplayCurrency(jobPost.compensation?.baseMaximum)
      : `${formatDisplayCurrency(jobPost.compensation?.baseMinimum)} - ${formatDisplayCurrency(
          jobPost.compensation?.baseMaximum
        )}`;

  const totalAnticipatedDisplay = formatDisplayCurrency(jobPost.compensation?.totalAnticipated);

  const analyticsSection = {
    heading: "Analytics",
    options: [
      {
        overline: "Number of Applicants",
        headline: jobPost.numberOfApplicants?.toString(),
        iconName: "group" as const
      },
      {
        overline: "Unique Views (Last 90 Days)",
        headline: uniqueViewsHeadline,
        iconName: "search" as const
      }
    ].filter((option) => !emptyOptions.some((empty) => String(option.headline).includes(empty)))
  };

  const compensationSection = {
    heading: "Compensation",
    options: [
      {
        overline: "Base Compensation",
        headline: `${
          baseCompensationDisplay ? `$${baseCompensationDisplay} / ${jobPost.paySchedule?.toLowerCase()}` : ""
        }`,
        iconName: "attach_money" as const
      },
      {
        overline: "Total Anticipated Compensation",
        headline: totalAnticipatedDisplay ? `$${totalAnticipatedDisplay}` : "",
        iconName: "attach_money" as const
      },
      {
        overline: "Compensation Visibility",
        headline: jobPost.hideCompensationFromJobBoard ? "Hidden" : "Visible",
        iconName: "visibility" as const
      }
    ].filter((option) => !emptyOptions.some((empty) => String(option.headline).includes(empty)))
  };

  const sections = [
    {
      heading: "Job Details",
      options: [
        {
          overline: "Job Title",
          headline: <MarkdownContent content={jobPost.name} preserveHeadingStyle={true} />,
          iconName: "business_center" as const
        },
        {
          overline: "Job Category",
          headline: `${jobPost.category?.area} - ${jobPost.category?.type}`,
          iconName: "business_center" as const
        },
        {
          overline: "Position Type",
          headline: jobPost.positionType,
          iconName: "calendar_today" as const
        },
        {
          overline: "Applicant Group",
          headline: jobPost.applicantGroup,
          iconName: "group" as const
        },
        {
          overline: "Service Level",
          headline: jobPost.serviceLevel,
          iconName: "handshake" as const
        },
        {
          overline: "Application Via",
          headline: jobPost.applyVia,
          iconName: "link" as const,
          ...(applicationUrl && {
            action: () => window.open(applicationUrl, "_blank", "noopener,noreferrer"),
            actionIcon: "open_in_new" as const
          })
        },
        ...((jobPost.serviceLevel === "Advanced" || jobPost.serviceLevel === "Executive") && jobPost.applicationFormUrl
          ? [
              {
                overline: "Job Application Form",
                headline:
                  jobPost.applicationFormUrl && jobPost.applicationFormUrl.includes("application-form")
                    ? jobPost.applicationForm?.questions.length === 0
                      ? "No Additional Questions"
                      : `${jobPost.applicationForm?.questions.length} Custom Questions`
                    : "Custom Salesforce Page",
                iconName: "link" as const,
                ...(jobPost.applicationFormUrl && {
                  action: () => window.open(jobPost.applicationFormUrl, "_blank", "noopener,noreferrer"),
                  actionIcon: "open_in_new" as const
                })
              }
            ]
          : []),
        {
          overline: "Employer",
          headline: jobPost.employer?.name || jobPost.pointOfContact?.name,
          iconName: "person" as const,
          ...(jobPost.employer?.pgaId && {
            action: () => window.open(`/people/${jobPost.employer?.pgaId}/person/overview`, "_blank"),
            actionIcon: "open_in_new" as const
          })
        }
      ].filter((option) => !emptyOptions.some((empty) => String(option.headline).includes(empty)))
    },
    {
      heading: "Dates",
      options: [
        {
          overline: "Application Deadline",
          headline: toDateFormat(jobPost.submissionDeadline?.toString(), {
            tz: "America/New_York"
          }),
          iconName: "today" as const
        },
        // stage dependent date display for go-live time and last published time
        ...([PostStage.PENDING, PostStage.ASSIST].includes(jobPost.stage as PostStage)
          ? [
              {
                overline: "Job Post Go-Live Time", // future scheduled
                headline: jobPost.goLiveTime ? toDateTimeFormat(jobPost.goLiveTime.toString()) : "Not scheduled",
                iconName: "today" as const
              }
            ]
          : [PostStage.ACTIVE, PostStage.INACTIVE, PostStage.REMOVED, PostStage.DRAFT, PostStage.IN_REVIEW].includes(
                jobPost.stage as PostStage
              )
            ? [
                {
                  overline: "Job Post Last Published", // last published to job board
                  headline: jobPost.jobPostPublishTime
                    ? toDateTimeFormat(jobPost.jobPostPublishTime.toString())
                    : "Not published",
                  iconName: "today" as const
                }
              ]
            : []),
        {
          overline: "Last Modified",
          headline: toDateTimeFormat(jobPost.updatedAt?.toString()),
          iconName: "calendar_today" as const
        },
        ...(jobPost.closedDate
          ? [
              {
                overline: "Job Post Closed Date",
                headline: toDateTimeFormat(jobPost.closedDate.toString()) ?? "--",
                iconName: "today" as const
              }
            ]
          : [])
      ].filter((option) => !emptyOptions.some((empty) => String(option.headline).includes(empty)))
    }
  ];

  const locationSection = {
    heading: "Location",
    options: [
      {
        overline: "Managing Section",
        headline: jobPost.section?.name,
        iconName: "domain" as const
      },
      {
        overline: "Facility",
        headline: jobPost.facility?.name,
        supportingText: formatAddress(jobPost?.facility?.mainAddress),
        iconName: "location_on" as const,
        ...(jobPost.facility?.id && {
          action: () =>
            window.open(
              generatePath(`/facilities/:facilityId/details`, {
                facilityId: jobPost.facility!.id
              }) as Path,
              "_blank",
              "noopener,noreferrer"
            ),
          actionIcon: "open_in_new" as const
        })
      },
      ...(jobPost.facility?.id
        ? [
            {
              overline: "Facility Permissions Roles",
              headline: "Manage Jobs",
              iconName: "group" as const,
              action: () =>
                window.open(
                  generatePath(`/facilities/:facilityId/jobs/permissions`, {
                    facilityId: jobPost.facility!.id
                  }) as Path,
                  "_blank",
                  "noopener,noreferrer"
                ),
              actionIcon: "open_in_new" as const
            }
          ]
        : []),
      {
        overline: "Promoted in Additional Sections",
        headline: jobPost.otherSections?.length ? jobPost.otherSections.join(", ") : <p className="opacity-20">--</p>,
        iconName: "domain" as const
      },
      {
        overline: "Promoted in Additional States",
        headline: jobPost.otherStates?.length ? jobPost.otherStates.join(", ") : <p className="opacity-20">--</p>,
        iconName: "map" as const
      }
    ].filter((option) =>
      option.overline === "Managing Section" || option.overline === "Facility"
        ? !emptyOptions.some((empty) => String(option.headline).includes(empty))
        : true
    )
  };

  const educationAndExperienceSection = {
    heading: "Education and Experience",
    options: [
      {
        overline: "Years Experience",
        headline: jobPost.requiredExperienceInYears ? `${jobPost.requiredExperienceInYears} years` : "Not Applicable",
        iconName: "calendar_today" as const
      },
      {
        overline: "Education",
        headline: jobPost.requiredEducation || "Not Applicable",
        iconName: "school" as const
      },
      {
        overline: "PGA Learning",
        headline: jobPost.additionalPgaEducation || "Not Applicable",
        iconName: "sports_golf" as const
      }
    ].filter((option) => !emptyOptions.some((empty) => String(option.headline).includes(empty)))
  };

  return (
    <div className="flex flex-col gap-4">
      {analyticsSection.options.length > 0 && (
        <DetailsCard heading={analyticsSection.heading} options={analyticsSection.options} />
      )}

      {compensationSection.options.length > 0 && (!jobPost.hideCompensationFromJobBoard || canManageJobPosts) && (
        <DetailsCard heading={compensationSection.heading} options={compensationSection.options} />
      )}

      <MultiSectionDetailsCard sections={sections} />

      {locationSection.options.length > 0 && (
        <DetailsCard heading={locationSection.heading} options={locationSection.options} />
      )}

      {educationAndExperienceSection.options.length > 0 && (
        <DetailsCard heading={educationAndExperienceSection.heading} options={educationAndExperienceSection.options} />
      )}
    </div>
  );
}
